import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { onboardingApi } from '@/api/resources';
import { useAuthStore } from '@/store/auth';
import type { OnboardingStatusResponse } from '@/types/api';

export const ONBOARDING_KEY = ['onboarding'] as const;

/** How often the wizard re-asks while a reviewer has the next move. The page quotes it. */
export const ONBOARDING_POLL_MS = 30_000;

/**
 * The authority on what the app may do.
 *
 * Permissions no longer answer that question: a founder holds the full
 * ORG_OWNER set from the moment they register, before anyone has verified the
 * organization, and every endpoint outside registration answers
 * ORGANIZATION_NOT_VERIFIED until an admin approves. So the shell asks once on
 * load and branches on `restricted` — it never probes an endpoint and infers
 * access from a 403.
 *
 * Approval opens the gate with no re-login and no refresh, so the answer is
 * re-checked on navigation and polled while the caller is waiting on someone
 * else.
 */
export function useOnboarding() {
  const authenticated = useAuthStore((s) => s.phase === 'authenticated');
  const queryClient = useQueryClient();

  const query = useQuery<OnboardingStatusResponse>({
    queryKey: ONBOARDING_KEY,
    queryFn: onboardingApi.get,
    enabled: authenticated,
    staleTime: 15_000,
    // Nothing the user can do moves this along; a reviewer does.
    refetchInterval: (q) => (q.state.data?.step === 'AWAITING_REVIEW' ? ONBOARDING_POLL_MS : false),
    refetchOnWindowFocus: true,
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY }),
    [queryClient],
  );

  return {
    status: query.data,
    /** Undefined until the first answer — treat as "unknown", not "allowed". */
    restricted: query.data?.restricted,
    step: query.data?.step,
    isLoading: query.isLoading,
    error: query.error,
    refresh,
  };
}
